import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Pencil, Trash2 } from "lucide-react";

import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Skeleton } from "../feedback/Skeleton";
import { EmptyState } from "../feedback/EmptyState";
import { Pagination } from "../ui/Pagination";

/**
 * Generic data table with row actions, sort, pagination, and search.
 * Used by every admin master-data list page; kept role-agnostic so
 * standard-user lists can adopt it later.
 *
 * columns: [{ key, labelKey, sortable?, className?, render?(row) }]
 */
export function DataTable({
  columns,
  items,
  loading,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sortKey,
  sortDir,
  onSortChange,
  searchValue,
  onSearchChange,
  onEdit,
  onDelete,
  entityTestId,
  emptyBody,
  actionLabels = {},
}) {
  const { t } = useTranslation();
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const isSorted = (key) => key === sortKey;
  const toggleSort = (col) => {
    if (!col.sortable) return;
    if (col.key === sortKey) {
      onSortChange(col.key, sortDir === "asc" ? "desc" : "asc");
    } else {
      onSortChange(col.key, "asc");
    }
  };

  const editLabel = actionLabels.edit ?? t("admin.common.edit");
  const deleteLabel = actionLabels.delete ?? t("admin.common.delete");

  return (
    <div className="space-y-3" data-testid={`${entityTestId}-table-wrapper`}>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t("admin.common.search_placeholder")}
          data-testid={`${entityTestId}-search`}
          className="h-10 max-w-sm bg-surface border-border-subtle focus-visible:ring-champagne"
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-border-subtle bg-surface shadow-card">
        <table className="min-w-full text-sm" data-testid={`${entityTestId}-table`}>
          <thead className="bg-offwhite">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  onClick={() => toggleSort(col)}
                  className={[
                    "px-4 py-3 text-left text-[11px] uppercase tracking-wide text-text-secondary",
                    col.sortable ? "cursor-pointer select-none" : "",
                    col.className || "",
                  ].join(" ")}
                  data-testid={`${entityTestId}-th-${col.key}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {t(col.labelKey)}
                    {col.sortable && isSorted(col.key) && (
                      sortDir === "asc" ? (
                        <ChevronUp className="h-3 w-3" />
                      ) : (
                        <ChevronDown className="h-3 w-3" />
                      )
                    )}
                  </span>
                </th>
              ))}
              <th className="w-24 px-4 py-3 text-right text-[11px] uppercase tracking-wide text-text-secondary">
                {t("admin.common.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3">
                      <Skeleton className="h-4 w-32" />
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <Skeleton className="h-4 w-16" />
                  </td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="px-4 py-10">
                  <EmptyState
                    title={
                      searchValue
                        ? t("admin.common.no_search_results")
                        : t("admin.common.no_results")
                    }
                    body={emptyBody || ""}
                    testId={`${entityTestId}-empty`}
                  />
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr
                  key={row.id}
                  data-testid={`${entityTestId}-row-${row.id}`}
                  className="hover:bg-offwhite/50"
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-4 py-3 align-top ${col.className || ""}`}
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {onEdit && (
                        <button
                          type="button"
                          onClick={() => onEdit(row)}
                          aria-label={editLabel}
                          title={editLabel}
                          data-testid={`${entityTestId}-edit-${row.id}`}
                          className="rounded p-1.5 text-text-secondary hover:bg-offwhite hover:text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {onDelete && (
                        <button
                          type="button"
                          onClick={() => onDelete(row)}
                          aria-label={deleteLabel}
                          title={deleteLabel}
                          data-testid={`${entityTestId}-delete-${row.id}`}
                          className="rounded p-1.5 text-text-secondary hover:bg-status-unavailable/10 hover:text-status-unavailable focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <Pagination
          page={page}
          totalPages={Math.max(1, Math.ceil(total / pageSize))}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          rangeStart={from}
          rangeEnd={to}
          total={total}
          testId={`${entityTestId}-pagination`}
        />
      )}
    </div>
  );
}
